import { useEffect, useRef } from "react";
import type { MapMarker } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import BizListItem from "./BizListItem";

interface Props {
  markers: MapMarker[];
  total: number;
  loading: boolean;
  error: string | null;
  selectedNo: number | null;
  colorMap: Record<string, string>;
  onSelect: (no: number) => void;
  onRetry: () => void;
  onShowAll: () => void;
}

export default function BizMarkerList({
  markers,
  total,
  loading,
  error,
  selectedNo,
  colorMap,
  onSelect,
  onRetry,
  onShowAll,
}: Props) {
  const listRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    if (selectedNo == null || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-no="${selectedNo}"]`);
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedNo]);

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-12 text-center">
        <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-error-warning-line text-xl" aria-hidden="true"></i>
        </span>
        <p className="text-sm font-semibold text-foreground-800">목록을 불러오지 못했습니다</p>
        <p className="max-w-xs text-xs text-foreground-500">{error}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-1 rounded-full bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 hover:bg-primary-600 transition-colors"
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
        <p className="text-sm font-semibold text-foreground-800">
          사업장 <span className="text-primary-600">{markers.length}</span>
          {typeof total === "number" && total > markers.length && (
            <span className="ml-1 text-xs font-normal text-foreground-500">/ 전체 {total}</span>
          )}
        </p>
        <span className="text-[11px] text-foreground-500">가까운 순</span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {loading ? (
          <div className="flex flex-col gap-2.5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-lg border border-background-200 p-3.5">
                <Skeleton className="h-4 w-1/3 rounded" />
                <Skeleton className="mt-2 h-3 w-1/4 rounded" />
                <Skeleton className="mt-2 h-3 w-2/3 rounded" />
              </div>
            ))}
          </div>
        ) : markers.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
            <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-map-pin-line text-xl" aria-hidden="true"></i>
            </span>
            <p className="text-sm font-medium text-foreground-700">이 지역에 등록된 회원 사업장이 없습니다</p>
            <p className="text-xs text-foreground-500">
              업종을 변경하거나 지도를 <br className="hidden sm:block" />
              이동한 뒤 재검색해 보세요.
            </p>
            <button
              type="button"
              onClick={onShowAll}
              className="mt-2 rounded-full border border-primary-200 bg-primary-50 px-4 py-1.5 text-xs font-semibold text-primary-700 hover:bg-primary-100 transition-colors"
            >
              전체 보기
            </button>
          </div>
        ) : (
          <div ref={listRef} role="list" className="flex flex-col gap-2.5">
            {markers.map((m) => (
              <BizListItem
                key={m.no}
                marker={m}
                colorMap={colorMap}
                selected={m.no === selectedNo}
                onSelect={onSelect}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}